import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar,
  Plus,
  Check,
  X,
  Clock,
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import usePermissions from '@/hooks/usePermissions';
import { useHRNotifications } from '@/hooks/useHRNotifications';
import { DateInput } from '@/components/ui/date-input';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LeaveManagementPage = () => {
  const { user } = useAuth();
  const { hasPermission, isAdmin } = usePermissions();
  const { showNotification, hasPermission: hasNotifPermission } = useHRNotifications(user);
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('all');
  
  // Apply Leave Dialog
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    leave_type: 'paid',
    start_date: '',
    end_date: '',
    reason: ''
  });
  
  // Approve/Reject Dialog
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  const canApproveLeave = isAdmin || hasPermission('hr.approve_leave');
  const canApplyLeave = hasPermission('hr.apply_leave');

  const fetchRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      // If not admin/HR, only show own requests
      if (!canApproveLeave) {
        params.append('user_id', user.id);
      }
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (leaveTypeFilter !== 'all') params.append('leave_type', leaveTypeFilter);
      
      const response = await axios.get(`${API_BASE_URL}/hr/leave/requests?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.requests);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, leaveTypeFilter, canApproveLeave, user?.id]);

  const fetchLeaveBalance = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/hr/leave/balance/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaveBalance(response.data);
    } catch (error) {
      console.error('Error fetching leave balance:', error);
    }
  }, [user?.id]);

  useEffect(() => {
    Promise.all([fetchRequests(), fetchLeaveBalance()]);
  }, [fetchRequests, fetchLeaveBalance]);

  const calculateDays = () => {
    if (!leaveForm.start_date || !leaveForm.end_date) return 0;
    const start = new Date(leaveForm.start_date);
    const end = new Date(leaveForm.end_date);
    return Math.max(0, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
  };

  const handleApplyLeave = async () => {
    if (!leaveForm.start_date || !leaveForm.end_date || !leaveForm.reason) {
      toast.error('Please fill all required fields');
      return;
    }

    const days = calculateDays();
    if (days <= 0) {
      toast.error('End date must be after start date');
      return;
    }

    setApplyLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/hr/leave/apply?user_id=${user.id}`,
        leaveForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Leave request submitted');
      setApplyDialogOpen(false);
      setLeaveForm({ leave_type: 'paid', start_date: '', end_date: '', reason: '' });
      fetchRequests();
      fetchLeaveBalance();
      
      // Trigger notification for admins (they will see it via backend notification system)
      // The backend already creates a notification for admins when leave is applied
    } catch (error) {
      console.error('Error applying leave:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit leave request');
    } finally {
      setApplyLoading(false);
    }
  };

  const handleReviewRequest = (request) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setReviewDialogOpen(true);
  };

  const submitReview = async (approved) => {
    if (!selectedRequest) return;
    
    if (!approved && !rejectionReason) {
      toast.error('Please provide rejection reason');
      return;
    }

    setReviewLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/hr/leave/${selectedRequest.id}/approve?approver_id=${user.id}`,
        {
          status: approved ? 'approved' : 'rejected',
          rejection_reason: rejectionReason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Leave request ${approved ? 'approved' : 'rejected'}`);
      setReviewDialogOpen(false);
      fetchRequests();
      
      // Show browser notification for the approver (the employee gets notified via backend)
      if (hasNotifPermission) {
        showNotification('leave_status', {
          status: approved ? 'approved' : 'rejected',
          leaveType: selectedRequest.leave_type
        });
      }
    } catch (error) {
      console.error('Error reviewing request:', error);
      toast.error('Failed to process request');
    } finally {
      setReviewLoading(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-500/20 text-yellow-400', icon: Clock, label: 'Pending' },
      approved: { color: 'bg-green-500/20 text-green-400', icon: CheckCircle, label: 'Approved' },
      rejected: { color: 'bg-red-500/20 text-red-400', icon: XCircle, label: 'Rejected' },
      cancelled: { color: 'bg-gray-500/20 text-gray-400', icon: X, label: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  const LeaveTypeBadge = ({ type }) => {
    const typeConfig = {
      paid: { color: 'bg-green-500/20 text-green-400', label: 'Paid Leave' },
      sick: { color: 'bg-blue-500/20 text-blue-400', label: 'Sick Leave' },
      unpaid: { color: 'bg-gray-500/20 text-gray-400', label: 'Unpaid Leave' },
      maternity: { color: 'bg-pink-500/20 text-pink-400', label: 'Maternity' },
      paternity: { color: 'bg-purple-500/20 text-purple-400', label: 'Paternity' },
      emergency: { color: 'bg-red-500/20 text-red-400', label: 'Emergency' }
    };

    const config = typeConfig[type] || typeConfig.unpaid;

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leave-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">Leave Management</h1>
          <p className="text-wt-text-muted">
            {canApproveLeave ? 'Manage all leave requests' : 'Apply and track your leave'}
          </p>
        </div>
        <div className="flex gap-2">
          {canApproveLeave && pendingCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <AlertCircle className="h-4 w-4 text-yellow-400" />
              <span className="text-sm text-yellow-400">{pendingCount} pending</span>
            </div>
          )}
          {canApplyLeave && (
            <Button
              onClick={() => setApplyDialogOpen(true)}
              className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              data-testid="apply-leave-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Apply Leave
            </Button>
          )}
        </div>
      </div>

      {/* Leave Balance Card */}
      {leaveBalance && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-wt-surface border-wt-border">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-wt-text-muted">Paid Leave</p>
              <p className="text-3xl font-bold text-green-400">{leaveBalance.paid_leave}</p>
              <p className="text-xs text-wt-text-muted">days remaining</p>
            </CardContent>
          </Card>
          <Card className="bg-wt-surface border-wt-border">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-wt-text-muted">Sick Leave</p>
              <p className="text-3xl font-bold text-blue-400">{leaveBalance.sick_leave}</p>
              <p className="text-xs text-wt-text-muted">days remaining</p>
            </CardContent>
          </Card>
          <Card className="bg-wt-surface border-wt-border">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-wt-text-muted">Unpaid Leave</p>
              <p className="text-3xl font-bold text-gray-400">{leaveBalance.unpaid_leave}</p>
              <p className="text-xs text-wt-text-muted">no limit</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-wt-surface border-wt-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-wt-bg border-wt-border">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent className="bg-wt-surface border-wt-border">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select value={leaveTypeFilter} onValueChange={setLeaveTypeFilter}>
                <SelectTrigger className="bg-wt-bg border-wt-border">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent className="bg-wt-surface border-wt-border">
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="paid">Paid Leave</SelectItem>
                  <SelectItem value="sick">Sick Leave</SelectItem>
                  <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leave Requests List */}
      <div className="space-y-3">
        {requests.length === 0 ? (
          <Card className="bg-wt-surface border-wt-border">
            <CardContent className="py-12 text-center">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-wt-text-muted opacity-50" />
              <p className="text-wt-text-muted">No leave requests found</p>
            </CardContent>
          </Card>
        ) : (
          requests.map((request) => (
            <motion.div
              key={request.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className={`bg-wt-surface border-wt-border ${
                request.status === 'pending' ? 'border-yellow-500/30' : ''
              }`}>
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 rounded-full bg-wt-gold/20 flex items-center justify-center">
                        <Calendar className="h-6 w-6 text-wt-gold" />
                      </div>
                      <div>
                        {canApproveLeave && (
                          <p className="font-medium text-wt-text-primary">{request.user_name}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <LeaveTypeBadge type={request.leave_type} />
                          <span className="text-sm text-wt-text-muted">
                            {request.start_date} to {request.end_date}
                          </span>
                          <span className="text-sm text-wt-text-muted">
                            ({request.total_days} days)
                          </span>
                        </div>
                        <p className="text-sm text-wt-text-secondary mt-2 line-clamp-2">
                          {request.reason}
                        </p>
                        {request.status === 'rejected' && request.rejection_reason && (
                          <p className="text-sm text-red-400 mt-1">
                            Rejection: {request.rejection_reason}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={request.status} />
                      {canApproveLeave && request.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => handleReviewRequest(request)}
                          className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                        >
                          Review
                        </Button>
                      )}
                    </div>
                  </div>
                  {request.approved_by_name && (
                    <div className="mt-3 pt-3 border-t border-wt-border text-xs text-wt-text-muted">
                      {request.status === 'approved' ? 'Approved' : 'Rejected'} by {request.approved_by_name} on{' '}
                      {new Date(request.approved_at).toLocaleDateString()}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Apply Leave Dialog */}
      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Apply for Leave</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Leave Type</Label>
              <Select
                value={leaveForm.leave_type}
                onValueChange={(value) => setLeaveForm({...leaveForm, leave_type: value})}
              >
                <SelectTrigger className="bg-wt-bg border-wt-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-surface border-wt-border">
                  <SelectItem value="paid">Paid Leave ({leaveBalance?.paid_leave || 0} days left)</SelectItem>
                  <SelectItem value="sick">Sick Leave ({leaveBalance?.sick_leave || 0} days left)</SelectItem>
                  <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                  <SelectItem value="emergency">Emergency Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <DateInput
                  value={leaveForm.start_date}
                  onChange={(e) => setLeaveForm({...leaveForm, start_date: e.target.value})}
                  className="bg-wt-bg border-wt-border"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <DateInput
                  value={leaveForm.end_date}
                  onChange={(e) => setLeaveForm({...leaveForm, end_date: e.target.value})}
                  className="bg-wt-bg border-wt-border"
                  min={leaveForm.start_date || new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            {leaveForm.start_date && leaveForm.end_date && (
              <div className="p-3 rounded-lg bg-wt-bg border border-wt-border text-center">
                <span className="text-wt-text-muted">Total: </span>
                <span className="text-xl font-bold text-wt-gold">{calculateDays()}</span>
                <span className="text-wt-text-muted"> days</span>
              </div>
            )}

            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea
                value={leaveForm.reason}
                onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})}
                placeholder="Please provide reason for leave..."
                className="bg-wt-bg border-wt-border min-h-24"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setApplyDialogOpen(false)}
              className="border-wt-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleApplyLeave}
              className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              disabled={applyLoading}
            >
              {applyLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">
              Review Leave Request
            </DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-wt-bg border border-wt-border space-y-2">
                <div className="flex justify-between">
                  <span className="text-wt-text-muted">Employee</span>
                  <span className="text-wt-text-primary font-medium">{selectedRequest.user_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-wt-text-muted">Type</span>
                  <LeaveTypeBadge type={selectedRequest.leave_type} />
                </div>
                <div className="flex justify-between">
                  <span className="text-wt-text-muted">Dates</span>
                  <span className="text-wt-text-primary">
                    {selectedRequest.start_date} - {selectedRequest.end_date}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-wt-text-muted">Duration</span>
                  <span className="text-wt-text-primary font-medium">{selectedRequest.total_days} days</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Reason</Label>
                <p className="p-3 rounded-lg bg-wt-bg border border-wt-border text-sm text-wt-text-secondary">
                  {selectedRequest.reason}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Rejection Reason (if rejecting)</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Provide reason for rejection..."
                  className="bg-wt-bg border-wt-border"
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => submitReview(false)}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              disabled={reviewLoading}
            >
              <X className="h-4 w-4 mr-1" />
              Reject
            </Button>
            <Button
              onClick={() => submitReview(true)}
              className="bg-green-500 hover:bg-green-600 text-white"
              disabled={reviewLoading}
            >
              <Check className="h-4 w-4 mr-1" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveManagementPage;
